import React from "react";
import { ShieldCheck, AlertTriangle, Clock, ExternalLink, Bookmark, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";

function formatDate(iso, language) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString(language === "hi" ? "hi-IN" : "en-IN", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export function TrustFooter({ metadata, onSave, saved, isAuthed, showSave = true }) {
  const { t, language } = useLanguage();
  if (!metadata) return null;
  const highRisk = metadata.risk_level === "high" || metadata.requires_verification;
  const isSafeUrl = (u) => typeof u === "string" && /^https?:\/\//i.test(u);
  const sources = (metadata.sources || []).filter((s) => isSafeUrl(s.url));

  return (
    <div className="mt-3 border-t border-slate-100 pt-3" data-testid="trust-footer">
      {highRisk && metadata.disclaimer && (
        <div className="mb-3 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="ai-verify-note">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div><span className="font-semibold">{t("result_verify")}. </span>{metadata.disclaimer}</div>
        </div>
      )}

      {sources.length > 0 && (
        <div className="mb-3 rounded-xl bg-slate-50 p-3" data-testid="suggested-sources">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {metadata.web_verified ? t("web_sources_title") : t("suggested_sources")}
            {metadata.web_verified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid="web-verified-badge">
                <ShieldCheck className="h-3 w-3" /> {t("web_verified_badge")}
              </span>
            )}
          </p>
          <ul className="mt-2 space-y-1.5">
            {sources.map((s) => (
              <li key={s.url} className="flex flex-col">
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid={`source-link-${s.url}`}>
                  {s.name} <ExternalLink className="h-3 w-3" />
                </a>
                {metadata.web_verified && (
                  <span className="text-xs text-slate-400">{t("source_date")}: {s.date || "—"}</span>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-slate-400">{metadata.web_verified ? t("web_sources_note") : t("sources_note")}</p>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${highRisk ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="ai-risk-badge">
            {highRisk ? <AlertTriangle className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
            {highRisk ? t("risk_high") : t("risk_low")}
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-slate-400">
            <Clock className="h-3 w-3" /> {t("generated_on")} {formatDate(metadata.updated_at, language)}
          </span>
        </div>
        {showSave && onSave && (
          <Button type="button" variant={saved ? "outline" : "secondary"} size="sm" onClick={onSave} disabled={saved} data-testid="ai-save-btn" className="rounded-full">
            {saved ? (<span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> {t("result_saved")}</span>)
              : (<span className="flex items-center gap-1.5"><Bookmark className="h-3.5 w-3.5" /> {isAuthed ? t("result_save") : t("result_login_to_save")}</span>)}
          </Button>
        )}
      </div>
    </div>
  );
}
